import React, { useEffect, useState } from "react";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore"; // Importar funciones de Firestore
import { db } from "../../../firebase/firebaseConfig"; // Importar la configuración de Firebase
import { IconButton, Slider, Box, Typography, List, ListItem, ListItemText, ListItemAvatar, Avatar, CircularProgress, ListItemButton } from "@mui/material";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PauseIcon from "@mui/icons-material/Pause";
import EditIcon from '@mui/icons-material/EditNoteOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { getStorage, ref, deleteObject } from "firebase/storage";
import Alerta from "../../../components/Alerta";

const ListAudioForm = ({ onEditar }) => {
    const [audios, setAudios] = useState([]); // Estado para almacenar los audios
    const [loading, setLoading] = useState(true); // Estado para manejar la carga
    const [progresses, setProgresses] = useState({});  // Estado para manejar el progreso individual de cada canción
    const [isPlaying, setIsPlaying] = useState(false);        
    const [audioInstance, setAudioInstance] = useState(null); // Instancia del audio
    const [currentAudio, setCurrentAudio] = useState(null); // Estado para la canción actual
    const [volumes, setVolumes] = useState({}); // Estado para manejar el volumen individual de cada canción
    const [audioDurations, setAudioDurations] = useState({}); // Estado para manejar la duración de cada canción
    const [estadoAlerta, setEstadoAlerta] = useState(false);
    const [alerta, setAlerta] = useState({});

    // Función para manejar el cambio de volumen individual
    const handleVolumeChange = (id, newValue) => {
        const newVolume = newValue / 100;  // Convertir el valor del slider (0-100) a escala de volumen (0-1)

        setVolumes((prevVolumes) => ({
            ...prevVolumes,
            [id]: newVolume,  // Actualizar el volumen de la canción individual
        }));

        if (currentAudio === id && audioInstance) {
            audioInstance.volume = newVolume; // Aplicar el volumen al audio que está reproduciéndose
        }
    };

    const handleProgressChange = (id, newValue) => {
        setProgresses((prevProgresses) => ({
            ...prevProgresses,
            [id]: newValue,  // Actualizar el progreso de la canción individual
        }));

        if (audioInstance && currentAudio === id) {
            audioInstance.currentTime = (newValue / 100) * audioInstance.duration;  // Cambiar la posición del audio actual
        }
    };

    // Función para obtener la lista de audios desde Firestore
    useEffect(() => {
        const fetchAudios = async () => {
            try {
                const audiosCollectionRef = collection(db, "milistacancionescalocho");
                const audiosSnapshot = await getDocs(audiosCollectionRef);
                const audiosList = audiosSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setAudios(audiosList);
            } catch (error) {
                console.error("Error obteniendo audios:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchAudios();
    }, []);

    if (loading) {
        return <CircularProgress />;
    }

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    };

    // Función para manejar la reproducción y pausa
    const handlePlayPause = (audioUrl, id) => {
        try {
            if (audioInstance) {
                audioInstance.pause(); // Pausar la canción actual
            }

            if (currentAudio === id && isPlaying) {
                setIsPlaying(false);
            } else {
                const newAudio = new Audio(audioUrl);

                // Aplicar el volumen individual o un valor por defecto de 0.3
                const volumeToApply = volumes[id] !== undefined ? volumes[id] : 0.3;
                newAudio.volume = volumeToApply;

                // Guardar la duración de la canción cuando se carguen los metadatos
                newAudio.addEventListener('loadedmetadata', () => {
                    setAudioDurations((prevDurations) => ({
                        ...prevDurations,
                        [id]: newAudio.duration, // Guardar la duración de la canción
                    }));
                });

                // Actualizar el progreso conforme se reproduce la canción
                newAudio.addEventListener('timeupdate', () => {
                    const currentProgress = (newAudio.currentTime / newAudio.duration) * 100;
                    setProgresses((prevProgresses) => ({
                        ...prevProgresses,
                        [id]: currentProgress,  // Solo actualizar el progreso de la canción actual
                    }));
                });

                newAudio.play();
                setAudioInstance(newAudio);
                setCurrentAudio(id); // Identificar la canción actual
                setIsPlaying(true);
            }
        } catch (error) {
            console.error("Error al pausar o iniciar la canción:", error);
            alert("Hubo un problema al intentar reproducir la canción.");
        }
    };

    const handleEditar = (id) => {
        onEditar(id);
        console.log(id);
    }

    const handleEliminarMiMusica = async (id, urlImagen, audioURL) => {
        try {
            // 1. Eliminar el documento de Firestore
            await deleteDoc(doc(db, 'milistacancionescalocho', id));
            console.log('Documento eliminado de Firestore:', id);
    
            // 2. Crear una referencia a Firebase Storage
            const storage = getStorage();
    
            // 3. Eliminar la imagen (thumbnailURL)
            if (urlImagen) {
                const imageRef = ref(storage, urlImagen); // Asegúrate de que `urlImagen` sea la ruta relativa
                await deleteObject(imageRef).then(() => {
                    console.log('Imagen eliminada con éxito:', urlImagen);
                }).catch((error) => {
                    console.error('Error al eliminar la imagen:', error);
                    setAlerta({ tipo: 'error', mensaje: 'Error al eliminar la imagen.' });
                });
            }
    
            // 4. Eliminar el archivo de audio (audioURL)
            if (audioURL) {
                const audioRef = ref(storage, audioURL); // Asegúrate de que `audioURL` sea la ruta relativa
                await deleteObject(audioRef).then(() => {
                    console.log('Audio eliminado con éxito:', audioURL);
                }).catch((error) => {
                    console.error('Error al eliminar el audio:', error);
                    setAlerta({ tipo: 'error', mensaje: 'Error al eliminar el audio.' });
                });
            }
    
            // 5. Mostrar mensaje de éxito
            setEstadoAlerta(true);
            setAlerta({ tipo: 'exito', mensaje: 'Los datos y archivos fueron eliminados con éxito.' });
    
        } catch (error) {
            console.error('Error al eliminar el documento o archivos:', error);
            setEstadoAlerta(true);
            setAlerta({ tipo: 'error', mensaje: 'Hubo un error al eliminar el infomimusica.' });
        }
    }    
    

    return (
        <Box
            sx={{
                width: { xs: '100%', sm: '100%', md: '100%', lg: '100%' }, // Se ajusta en diferentes dispositivos
                maxWidth: 800,
                margin: 'auto',
                padding: { xs: 1, sm: 1 },
                boxShadow: 3,
                borderRadius: 2,
                height: 'auto',
                backgroundColor: '#fff',
            }}
        >
            {audios.length === 0 ? (
                <Typography variant="body1" align="center" sx={{ 
                    width: 'auto',
                    alignItems: 'center',
                    textAlign: 'center',
                    '@media (max-width: 600px)': {  // Ajustes para móviles
                        fontSize: '1.25rem',  // Tamaño más pequeño para la tipografía
                    },
                }}>
                    No hay audios subidos aún.
                </Typography>
            ) : (
                <List
                    sx={{
                        maxHeight: { xs: 500, sm: 350, md: 400 }, // Altura máxima variable
                        overflowY: 'auto',
                        paddingRight: 2,
                        '&::-webkit-scrollbar': {
                            width: '6px', // Barra de scroll más delgada
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(93, 3, 3, 0.7)',
                            borderRadius: '10px',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        }
                    }}
                >
                    {audios.map((audio) => (
                        <ListItem
                            key={audio.id}
                            sx={{
                                marginBottom: 2,
                                borderRadius: 2,
                                border: '0.5px solid #5d0303',
                                padding: { xs: 1, sm: 1.5, md: 2 }, // Ajuste de padding
                            }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: { xs: '100%', sm: '30%' }}}>
                                <ListItemAvatar>
                                    <Avatar 
                                    src={audio.thumbnailURL} 
                                    alt={audio.name} 
                                    variant="square"
                                    sx={{
                                        width: { xs: 60, sm: 70, md: 80 },
                                        height: { xs: 60, sm: 70, md: 80 },
                                        marginBottom: { xs: 1, sm: 0 },
                                    }}
                                />
                                </ListItemAvatar>
                                <ListItemText
                                    sx={{ 
                                        textAlign: 'center',
                                        alignContent: 'center',
                                        fontSize: { xs: '0.8rem', sm: '1rem' },
                                    }}
                                    primary={audio.name}
                                    secondary={<span style={{ color:'#000', textAlign: 'center' }}>Autor: {audio.author}</span>}
                                />
                            </Box>

                            {/* Controles del reproductor de música */}
                            <Box
                                sx={{
                                    width: { xs: '10%', sm: '85%', md: '80%', lg: '70%' },
                                    maxWidth: 500,
                                    padding: { xs: 1, sm: 2, md: 3 },
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'row'}}>
                                    {/* Icono de volumen y slider */}
                                    <VolumeUpIcon sx={{ mr: 2 }} />
                                    <Slider
                                        value={volumes[audio.id] !== undefined ? volumes[audio.id] * 100 : 30}  // Valor de volumen individual
                                        onChange={(event, newValue) => handleVolumeChange(audio.id, newValue)}  // Manejar el cambio de volumen individual
                                        aria-labelledby="volume-slider"
                                        sx={{
                                            color: '#5d0303',
                                            width: { xs: 50, sm: 150, md: 200 },
                                            mx: 2,
                                        }}
                                    />
                                </Box>

                                {/* Barra de progreso de la canción */}
                                <Box sx={{display: 'flex', flexDirection: 'row', width: '90%', }}>
                                    <Box
                                        sx={{
                                            width: { xs: '50%', sm: '85%', md: '80%', lg: '70%' },
                                            maxWidth: 500,
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Typography  sx={{ 
                                            width: { xs: '50%', sm: '85%', md: '80%', lg: '70%' },
                                            alignItems: 'center',
                                            textAlign: 'center',
                                            '@media (max-width: 600px)': {  // Ajustes para móviles
                                                width: 2,
                                                fontSize: '1.0rem',  // Tamaño más pequeño para la tipografía
                                            },
                                        }}>
                                            {formatTime(audioInstance && currentAudio === audio.id ? audioInstance.currentTime : 0)}
                                        </Typography>
                                        <Typography  sx={{ 
                                            width: { xs: '50%', sm: '85%', md: '80%', lg: '70%' },
                                            alignItems: 'center',
                                            textAlign: 'center',
                                            '@media (max-width: 600px)': {  // Ajustes para móviles
                                                width: 2,
                                                fontSize: '1.0rem',  // Tamaño más pequeño para la tipografía
                                            },
                                        }}>
                                            {formatTime(audioDurations[audio.id] || 0)}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Slider de progreso */}
                                <Slider
                                    value={progresses[audio.id] !== undefined ? progresses[audio.id] : 0}  // Valor de progreso individual
                                    onChange={(event, newValue) => handleProgressChange(audio.id, newValue)}  // Manejar el cambio de progreso individual
                                    aria-labelledby="progress-slider"
                                    sx={{
                                        color: '#5d0303',
                                        width: { xs: '50%', sm: '85%', md: '80%', lg: '70%' },
                                        mx: 2,
                                        marginTop: { xs: 1, sm: 2 },
                                    }}
                                />
                            </Box>

                            <Box sx={{
                                display: 'flex', 
                                flexDirection: 'row',
                                gap: '45px',
                            }}>
                                <IconButton
                                    aria-label="play/pause"
                                    sx={{ color: "#5d0303", fontSize: { xs: '1.5rem', sm: '2rem' } }}
                                    onClick={() => handlePlayPause(audio.audioURL, audio.id)}  // Pasar el ID para identificar la canción
                                >
                                    {isPlaying && currentAudio === audio.id ? <PauseIcon /> : <PlayArrowIcon />}
                                </IconButton>
                            </Box>

                            <Box sx={{
                                display: 'flex', 
                                flexDirection: 'column',
                                gap: '2px',
                            }}>
                                <ListItemButton>
                                        <EditIcon
                                            sx={{
                                                backgroundColor: '#f9f9f9',
                                                color: '#5d0303',
                                                borderRadius: '50%',
                                                width: { xs: 24, sm: 30 },
                                                height: { xs: 24, sm: 30 },
                                                '&:hover': {
                                                    backgroundColor: '#5d0303',
                                                    color: '#fff',
                                                }
                                            }} onClick={() => handleEditar(audio.id)}/>
                                </ListItemButton>
                                <ListItemButton>
                                        <DeleteIcon
                                        sx={{
                                            backgroundColor: '#f9f9f9',
                                            color: '#5d0303',
                                            borderRadius: '50%',
                                            width: { xs: 24, sm: 30 },
                                            height: { xs: 24, sm: 30 },
                                            '&:hover': {
                                                backgroundColor: '#5d0303',
                                                color: '#fff',
                                            }
                                        }} onClick={() => handleEliminarMiMusica(audio.id, audio.imagen, audio.audioURL)}/>
                                </ListItemButton>
                            </Box>

                            <Alerta 
                                tipo={alerta.tipo}
                                mensaje={alerta.mensaje}
                                estadoAlerta={estadoAlerta}
                                setAlerta={setEstadoAlerta}
                            />
                        </ListItem>
                    ))}
                </List>
            )}
        </Box>
    );
} 

export default ListAudioForm;